import { useCallback } from "react"
import type { CopilotSummary, LocalAudioRecord } from "../types"
import { settled } from "@/shared/lib/settled"
import { silentError } from "@/shared/lib/silentError"
import { deleteSession } from "@/lib/chunkDb"

type RenameFn = (args: {
    profileId: string
    fileId: string
    name: string
}) => Promise<unknown>

type DeleteFn = (args: { profileId: string; fileId: string }) => Promise<unknown>

type SetPending = (
    updater: (prev: Record<string, LocalAudioRecord[]>) => Record<string, LocalAudioRecord[]>,
) => void

/** Local pending-audio rename / delete / copilot UI patches (no machine). */
export function usePendingAudioActions(opts: {
    username: string | undefined
    getList: (profileId: string) => LocalAudioRecord[]
    setLocalAudioByProfileId: SetPending
    renameRecord: RenameFn
    deleteRecord: DeleteFn
}) {
    const { username, getList, setLocalAudioByProfileId, renameRecord, deleteRecord } = opts

    const renameLocalAudioRecord = useCallback(
        async (args: { profileId: string; localId: string; name: string }) => {
            const name = args.name.trim()
            if (!name) return
            const record = getList(args.profileId).find((r) => r.id === args.localId)
            if (username && record?.file_id && record.name !== name) {
                const renamed = await settled(
                    renameRecord({
                        profileId: args.profileId,
                        fileId: record.file_id,
                        name,
                    }),
                )
                if (!renamed.ok) return
            }
            setLocalAudioByProfileId((prev) => ({
                ...prev,
                [args.profileId]: (prev[args.profileId] ?? []).map((r) =>
                    r.id === args.localId ? { ...r, name } : r,
                ),
            }))
        },
        [getList, renameRecord, setLocalAudioByProfileId, username],
    )

    const deleteLocalAudioRecord = useCallback(
        async (args: { profileId: string; localId: string }) => {
            const removed = getList(args.profileId).find((r) => r.id === args.localId)
            if (!removed) return
            if (removed.file_id && username) {
                const deleted = await settled(
                    deleteRecord({ profileId: args.profileId, fileId: removed.file_id }),
                )
                if (!deleted.ok) return
            }
            try {
                URL.revokeObjectURL(removed.url)
            } catch {
                void 0
            }
            if (removed.sessionId) {
                deleteSession(removed.sessionId).then(undefined, (err) =>
                    silentError("indexedDb", err),
                )
            }
            setLocalAudioByProfileId((prev) => ({
                ...prev,
                [args.profileId]: (prev[args.profileId] ?? []).filter((r) => r.id !== args.localId),
            }))
        },
        [deleteRecord, getList, setLocalAudioByProfileId, username],
    )

    const setFinalizingRecord = useCallback(
        (copilotSessionId: string, isFinalizing: boolean) => {
            setLocalAudioByProfileId((prev) => {
                const updated: Record<string, LocalAudioRecord[]> = {}
                for (const [profileId, records] of Object.entries(prev)) {
                    updated[profileId] = records.map((r) =>
                        r.copilotSessionId === copilotSessionId ? { ...r, isFinalizing } : r,
                    )
                }
                return updated
            })
        },
        [setLocalAudioByProfileId],
    )

    const attachCopilotSummary = useCallback(
        (copilotSessionId: string, summary: CopilotSummary) => {
            setLocalAudioByProfileId((prev) => {
                const updated: Record<string, LocalAudioRecord[]> = {}
                for (const [profileId, records] of Object.entries(prev)) {
                    updated[profileId] = records.map((r) =>
                        r.copilotSessionId === copilotSessionId
                            ? {
                                  ...r,
                                  copilotSummary: summary,
                                  isFinalizing: false,
                                  summaryFailed: false,
                              }
                            : r,
                    )
                }
                return updated
            })
        },
        [setLocalAudioByProfileId],
    )

    const markSummaryFailed = useCallback(
        (copilotSessionId: string) => {
            setLocalAudioByProfileId((prev) => {
                const updated: Record<string, LocalAudioRecord[]> = {}
                for (const [profileId, records] of Object.entries(prev)) {
                    updated[profileId] = records.map((r) =>
                        r.copilotSessionId === copilotSessionId
                            ? { ...r, isFinalizing: false, summaryFailed: true }
                            : r,
                    )
                }
                return updated
            })
        },
        [setLocalAudioByProfileId],
    )

    return {
        renameLocalAudioRecord,
        deleteLocalAudioRecord,
        setFinalizingRecord,
        attachCopilotSummary,
        markSummaryFailed,
    }
}
